import { createAsyncThunk } from "@reduxjs/toolkit";
import { Base_URL, ApiCall } from "../../Common/Path";

export const GetStrategyVariance = createAsyncThunk(
  "GetStrategyVariance",
  async ({ strategyId }) => {
    try {
      let url = `${Base_URL}/strategies/variance`;
      
      if (strategyId) {
        url += `?strategyId=${strategyId}`;
      }
      
      const response = await ApiCall(url);
      return response;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
);

export const postRebalance = createAsyncThunk("postRebalance", async ({body}) => {
  try {
    let url = `${Base_URL}/strategies/rebalance`;
    const result = await ApiCall(url, body, "post");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});

export const getRebalance = createAsyncThunk(
  "getRebalance",
  async ({ type, status }) => {
    try {
      // Validate required parameters to prevent IDOR
     
      let url = `${Base_URL}/orders?sort=desc`;

      if (type != null) {
        url += `&type=${type}`;
      }

      if (status != null) {
        url += `&status=${status}`;
      }

      const result = await ApiCall(url, "", "get");
      return result;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
);

export const Taxlots = createAsyncThunk("Taxlots", async ({ role, strategyId, entityId }) => {
  try {
    let url = `${Base_URL}/lots?strategyId=${strategyId}`;
    const result = await ApiCall(url, "", "get");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});

export const postOrder = createAsyncThunk("postOrder", async (payload) => {
  try {
    let url = `${Base_URL}/orders`;
    const result = await ApiCall(url, payload, "PATCH");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});

export const postBulkOrder = createAsyncThunk("postBulkOrder", async (payload) => {
  try {
    let url = `${Base_URL}/orders/manager-discretion/bulk`;
    const result = await ApiCall(url, payload, "PATCH");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});

export const getAssetPrice = createAsyncThunk("getAssetPrice", async (body) => {
  try {
    let url = `${Base_URL}/assets/prices`;
    const result = await ApiCall(url, body, "post");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});

export const RebalanceLots = createAsyncThunk("RebalanceLots", async ({body}) => {
  try {
    let url = `${Base_URL}/strategies/rebalancing-preview/affected-lots`;
    const result = await ApiCall(url, body, "post");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});
export const ManualRebalanceLots = createAsyncThunk("ManualRebalanceLots", async ({body}) => {
  try {
    let url = `${Base_URL}/strategies/rebalance/manual`;
    const result = await ApiCall(url, body, "post");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});
export const CancelOrder = createAsyncThunk("CancelOrder", async ({ orderId }) => {
  try {
    // Validate orderId to prevent unauthorized access
    if (!orderId || typeof orderId !== 'string' || orderId.trim() === '') {
      throw new Error("Valid orderId is required");
    }
    
    let url = `${Base_URL}/orders/${orderId}`;
    const result = await ApiCall(url, "", "delete");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});

export const CancelPlacedOrder = createAsyncThunk("CancelPlacedOrder", async ({ orderId }) => {
  try {
    // Validate orderId to prevent unauthorized access
    if (!orderId || typeof orderId !== 'string' || orderId.trim() === '') {
      throw new Error("Valid orderId is required");
    }
    
    let url = `${Base_URL}/orders/${orderId}`;
    const result = await ApiCall(url, "", "delete");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});

export const postManualRebalance = createAsyncThunk("postManualRebalance", async ({body}) => {
  try {
    let url = `${Base_URL}/strategies/rebalance/manual`;
    const result = await ApiCall(url, body, "post");
    return result;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
});

// Get orders API with comprehensive filtering
export const getOrders = createAsyncThunk(
  "getOrders",
  async ({ 
    type = '', 
    status = 'PENDING', 
    sort = 'desc', 
    custodian = '' 
  }) => {
    try {
      // Build query parameters
      const params = [];
      if (type) params.push(`type=${encodeURIComponent(type)}`);
      if (status) params.push(`status=${status.toUpperCase()}`);
      if (sort) params.push(`sort=${sort}`);
      if (custodian) params.push(`custodian=${custodian.toUpperCase()}`);

      const url = `${Base_URL}/orders${params.length > 0 ? `?${params.join('&')}` : ''}`;
      
      const result = await ApiCall(url, "", "get");
      return result;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
);

// Get onboarded investors API
export const getOnboardedInvestors = createAsyncThunk(
  "getOnboardedInvestors",
  async ({ firmId, email = '' }) => {
    try {
      // Validate firmId
      if (!firmId || typeof firmId !== 'string' || firmId.trim() === '') {
        throw new Error("Valid firmId is required");
      }

      // Build URL with firm ID
      let url = `${Base_URL}/accounting/firms/${firmId}/onboarded-investors`;
      
      // Add email search parameter if provided
      if (email && email.trim() !== '') {
        url += `?email=${encodeURIComponent(email.trim())}`;
      }

      const result = await ApiCall(url, "", "get");
      return result;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
);

// Get investors by email API for Create Account Linkage modal
export const getInvestorsByEmail = createAsyncThunk(
  "getInvestorsByEmail",
  async ({ firmId, email }) => {
    try {
      // Validate email
      if (!email || typeof email !== 'string' || email.trim() === '') {
        throw new Error("Valid email is required");
      }
      if (!firmId || typeof firmId !== 'string' || firmId.trim() === '') {
        throw new Error("Valid firmId is required");
      }

      // Build URL with required firmId path param
      const url = `${Base_URL}/accounting/firms/${firmId}/user-roles-by-email?email=${encodeURIComponent(email.trim())}`;

      const result = await ApiCall(url, "", "get");
      return result;
    } catch (error) {
      throw error.response ? error.response.data : error.message;
    }
  }
);
